let isRefreshing = false;
let consecutiveFailures = 0;
let runningBuild: string | null = null;
let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

const BASE_POLL_INTERVAL = 90_000;
const MAX_POLL_INTERVAL = 15 * 60_000;

type NewVersionHandler = (info: { reload: () => void }) => void;

const defaultNewVersionHandler: NewVersionHandler = ({ reload }) => reload();

/**
 * Identifies the running build by the ETag of the served index.html, which changes whenever a
 * hashed asset filename inside it changes. Nothing needs stamping at build time, so this reports
 * the artifact actually being served rather than a number someone remembered to bump.
 */
async function fetchBuildId() {
  const response = await fetch('/', { method: 'HEAD', cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`index.html responded ${response.status}`);
  }

  return response.headers.get('etag');
}

async function recordRunningBuild() {
  try {
    runningBuild = await fetchBuildId();
  } catch (error) {
    console.error('Version check failed', error);
  }
}

export async function checkVersion(onNewVersion: NewVersionHandler = defaultNewVersionHandler) {
  // Skip if tab isn't visible — no point checking while user is away,
  if (document.visibilityState !== 'visible') {
    return;
  }

  if (isRefreshing) {
    return;
  }

  try {
    const latestBuild = await fetchBuildId();
    consecutiveFailures = 0;

    if (!latestBuild) {
      return;
    }

    if (!runningBuild) {
      runningBuild = latestBuild;
      return;
    }

    if (latestBuild === runningBuild) {
      return;
    }

    isRefreshing = true;
    console.log(`New deployment detected: ${runningBuild} -> ${latestBuild}`);

    onNewVersion({ reload: () => window.location.reload() });
  } catch (error) {
    consecutiveFailures++;
    console.error('Version check failed', error);
  }
}

let hasStartedPolling = false;

export function checkConfigVersion(onNewVersion?: NewVersionHandler) {
  if (hasStartedPolling) return;
  hasStartedPolling = true;

  recordRunningBuild();

  const scheduleNext = () => {
    // Exponential backoff: 90s → 3min → 6min → ... capped at 15min
    const interval = Math.min(
      BASE_POLL_INTERVAL * Math.pow(2, consecutiveFailures),
      MAX_POLL_INTERVAL
    );

    pollTimeoutId = setTimeout(async () => {
      await checkVersion(onNewVersion);
      scheduleNext();
    }, interval);
  };

  // Run once on tab regaining focus — catches the case where the user
  // came back after the app was idle through a deployment.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion(onNewVersion);
    }
  });

  scheduleNext();
}

const STALE_CHUNK_RELOADED_AT = 'stale-chunk-reloaded-at';
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/**
 * A deploy replaces every hashed chunk, so a tab still running the previous build fails its next
 * lazy import; reloading picks up the current build. At most one reload per cooldown, so a chunk
 * missing from the current build too reaches the route error boundary instead of looping.
 */
export function registerStaleChunkReload() {
  window.addEventListener('vite:preloadError', (event) => {
    const lastReloadAt = Number(sessionStorage.getItem(STALE_CHUNK_RELOADED_AT));
    if (Date.now() - lastReloadAt < STALE_CHUNK_RELOAD_COOLDOWN_MS) return;

    console.error('A chunk from a previous build failed to load. Reloading.', event.payload);
    sessionStorage.setItem(STALE_CHUNK_RELOADED_AT, String(Date.now()));
    window.location.reload();
  });
}
